import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, filter, of, timer } from 'rxjs';
import { catchError, concatMap, delay, map, switchMap, tap } from 'rxjs/operators';

import { ManagementHttpService } from '@core/http/management.http.service';
import { ProcessHttpService } from '@core/http/process.http.service';

import { VoivodeshipsHttpService } from '@core/http/voivodeships.http.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { FULL_PROCESS_GENERATION_IN_PROGRESS, NO_CHECKLIST_DOCUMENT_FOR_PROCESS } from '@constants';
import { parseError } from '@state/errors.parser';
import * as managementActions from './management.actions';

import { NzMessageRef } from 'ng-zorro-antd/message';
import { Router } from '@angular/router';
import { environment } from '@environment';

@Injectable()
export class ManagementEffects {
  private readonly actions$ = inject(Actions);
  private readonly managementHttp = inject(ManagementHttpService);

  private readonly processHttp = inject(ProcessHttpService);
  private readonly voivodeshipHttp = inject(VoivodeshipsHttpService);
  private readonly notification = inject(StackedMessagesService);

  private readonly router = inject(Router);

  searchUsersProcesses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.searchUsersProcesses),
      switchMap(({ payload }) => {
        return this.managementHttp.searchUsersProcesses(payload).pipe(
          map(processes => {
            return managementActions.searchUsersProcessesSuccess({ processes });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.searchUsersProcesses.type);
            this.notification.showError({ message: errorMessage });
            return of(managementActions.searchUsersProcessesError({ errorMessage }));
          })
        );
      })
    )
  );

  searchProcessesOfUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.searchProcessesOfUser),
      switchMap(({ payload }) => {
        return this.managementHttp.searchProcessesOfUser(payload).pipe(
          map(processesOfUser => {
            return managementActions.searchProcessesOfUserSuccess({ processesOfUser });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.searchProcessesOfUser.type);
            this.notification.showError({ message: errorMessage });
            return of(managementActions.searchProcessesOfUserError({ errorMessage }));
          })
        );
      })
    )
  );

  getUserProcessBasicData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getUserProcessBasicData),
      switchMap(({ userProcessId }) => {
        const request$ = this.managementHttp.getUserProcessBasicData({ userProcessId });
        const delay$ = timer((environment.minLoadingDataTime || 0.5) * 1000);
        return combineLatest([request$, delay$]).pipe(
          map(([userProcessBasicData]) => {
            return managementActions.getUserProcessBasicDataSuccess({ userProcessBasicData });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getUserProcessBasicData.type);
            return of(managementActions.getUserProcessBasicDataError({ errorMessage }));
          })
        );
      })
    )
  );

  addNewUserProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.addNewUserProcess),
      switchMap(({ payload }) => {
        return this.managementHttp.addNewUserProcess(payload).pipe(
          map(newUserProcess => {
            return managementActions.addNewUserProcessSuccess({ newUserProcess });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.addNewUserProcess.type);
            this.notification.showError({ message: errorMessage });
            return of(managementActions.addNewUserProcessError({ errorMessage }));
          })
        );
      })
    )
  );

  assignProcessType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.assignProcessType),
      switchMap(({ opts }) => {
        return this.managementHttp.assignProcessType(opts).pipe(
          map(userProcess => {
            return managementActions.assignProcessTypeSuccess({ userProcess });
          }),
          catchError(() => {
            this.notification.showError({ message: 'COMMON.ERROR.UNABLE_TO_ASSIGN_PROCESS_TYPE' });
            return of(managementActions.assignProcessTypeError());
          })
        );
      })
    )
  );

  getUserProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getUserProcess),
      switchMap(getOpts => {
        return this.managementHttp.getUserProcess(getOpts).pipe(
          map(userProcess => {
            if (!userProcess?.id) {
              this.notification.showError({ message: 'COMMON.ERROR.PROCESS_NOT_EXIST' });
              return managementActions.getUserProcessError({
                errorMessage: 'USER_PROCESS_NOT_FOUND_OR_HAS_BEEN_DELETED',
              });
            }
            return managementActions.getUserProcessSuccess({ userProcess });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getUserProcess.type);
            return of(managementActions.getUserProcessError({ errorMessage }));
          })
        );
      })
    )
  );

  getPersonalDetailsFullData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getPersonalDetailsFullData),
      switchMap(getOpts => {
        return this.managementHttp.getPersonalDetailsFullData(getOpts.userProcessId).pipe(
          map(personalDetails => {
            return managementActions.getPersonalDetailsFullDataSuccess({
              personalDetails,
              userProcessId: getOpts.userProcessId,
            });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getPersonalDetailsFullData.type);
            return of(managementActions.getPersonalDetailsFullDataError({ errorMessage }));
          })
        );
      })
    )
  );

  redirectOnGetUserProcessError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(managementActions.getUserProcessError),
        filter(action => action.errorMessage === 'USER_PROCESS_NOT_FOUND_OR_HAS_BEEN_DELETED'),
        tap(() => {
          this.router.navigate(['/']);
        })
      ),
    { dispatch: false }
  );

  removeUserProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.removeUserProcess),
      switchMap(({ userProcessId }) => {
        return this.managementHttp.removeUserProcess(userProcessId).pipe(
          map(() => {
            return managementActions.removeUserProcessSuccess({ removedUserProcessId: userProcessId });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.removeUserProcess.type);
            return of(managementActions.removeUserProcessError({ errorMessage }));
          })
        );
      })
    )
  );

  restoreUserProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.restoreUserProcess),
      switchMap(({ userProcessId }) => {
        return this.managementHttp.restoreUserProcess(userProcessId).pipe(
          map(updatedUserProcess => {
            return managementActions.restoreUserProcessSuccess({ updatedUserProcess });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.restoreUserProcess.type);
            return of(managementActions.restoreUserProcessError({ errorMessage }));
          })
        );
      })
    )
  );

  updateUserProcessBasicData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.updateUserProcessBasicData),
      switchMap(({ payloadWithUserId, userProcessId }) => {
        return this.managementHttp.updateUserProcessBasicData(userProcessId, payloadWithUserId).pipe(
          map(updatedUserProcess => {
            return managementActions.updateUserProcessBasicDataSuccess({ updatedUserProcess });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.updateUserProcessBasicData.type);
            this.notification.showError({ message: errorMessage });
            return of(managementActions.updateUserProcessBasicDataError({ errorMessage }));
          })
        );
      })
    )
  );

  getDetailsValidationConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getDetailsValidationConfig),
      switchMap(({ foreignerId, userProcessId }) => {
        return this.managementHttp.getPersonalDetailsValidationConfig(foreignerId, userProcessId).pipe(
          map(validationConfig =>
            managementActions.getDetailsValidationConfigSuccess({
              all: validationConfig.all,
              members: validationConfig.members,
              userProcessId,
            })
          ),
          catchError(() =>
            of(
              managementActions.getDetailsValidationConfigError({
                errorMessage: 'ERROR_FETCHING_DETAILS_VALIDATION_CONFIG',
              })
            )
          )
        );
      })
    )
  );

  getUserProcessDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getUserProcessDocuments),
      switchMap(getOpts => {
        return this.managementHttp.getUserProcessDocuments(getOpts).pipe(
          map(processDocuments => {
            return managementActions.getUserProcessDocumentsSuccess({
              processDocuments,
              userProcessId: getOpts.userProcessId,
            });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getUserProcessDocuments.type);
            return of(managementActions.getUserProcessDocumentsError({ errorMessage }));
          })
        );
      })
    )
  );

  updatePersonalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.updatePersonalDetails),
      switchMap(({ userId, personalDetails, userProcessId }) => {
        return this.managementHttp.updatePersonalDetailsInUserProcess({ userId, userProcessId, personalDetails }).pipe(
          map(updatedUserProcess => managementActions.updatePersonalDetailsSuccess({ userId, updatedUserProcess })),
          catchError(error => {
            // We don't have personal-details loading/error indicator anymore
            // So if update errors -> showing stacked message
            this.notification.showError({ message: 'COMMON.ERROR.ERROR_UPDATING_PERSONAL_DETAILS' });
            const errorMessage = parseError(error, managementActions.updatePersonalDetails.type);
            return of(managementActions.updatePersonalDetailsError({ errorMessage }));
          })
        );
      })
    )
  );

  setPersonalDetailsVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.setPersonalDetailsVerification),
      switchMap(({ userId, userProcessId, verified }) => {
        return this.managementHttp.setPersonalDetailsVerification({ userId, userProcessId, verified }).pipe(
          map(updatedUserProcess => managementActions.setPersonalDetailsVerificationSuccess({ updatedUserProcess })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.setPersonalDetailsVerification.type);
            return of(managementActions.setPersonalDetailsVerificationError({ errorMessage }));
          })
        );
      })
    )
  );

  setDocumentsSigningMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.setDocumentsSigningMode),
      switchMap(({ userId, userProcessId, mode }) => {
        return this.managementHttp.setDocumentsSigningMode({ userId, userProcessId, mode }).pipe(
          map(updatedUserProcess => managementActions.setDocumentsSigningModeSuccess({ updatedUserProcess })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.setDocumentsSigningMode.type);
            return of(managementActions.setDocumentsSigningModeError({ errorMessage }));
          })
        );
      })
    )
  );

  changeUserProcessStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.changeProcessStatus),
      switchMap(opts => {
        return this.managementHttp.changeUserProcessStatus(opts).pipe(
          map(updatedUserProcess => managementActions.changeProcessStatusSuccess({ updatedUserProcess })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.changeProcessStatus.type);
            return of(managementActions.changeProcessStatusError({ errorMessage }));
          })
        );
      })
    )
  );

  setUserDocumentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.setUserDocumentStatus),
      switchMap(opts => {
        return this.managementHttp.setUserDocumentStatus(opts).pipe(
          map(updatedUserDocument => managementActions.setUserDocumentStatusSuccess({ updatedUserDocument })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.setUserDocumentStatus.type);
            return of(managementActions.setUserDocumentStatusError({ errorMessage }));
          })
        );
      })
    )
  );

  generateDocumentsNow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.generateDocumentsNow),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.generateDocumentsNow(userId, userProcessId).pipe(
          switchMap(() => [
            managementActions.getDocumentsGenerationStatus({ userProcessId, userId }),
            managementActions.getPdfGenerationCurrentProgress({ userProcessId, userId }),
          ]),
          catchError(error => of(managementActions.generateDocumentsNowError({ error })))
        );
      })
    )
  );

  generateDocumentsNowError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(managementActions.generateDocumentsNowError),
        tap(({ error }) => {
          let errorName = error?.toString();
          if (error?.error?.message) {
            errorName = error.error?.message;
          }
          this.notification.showError({
            message: 'MANAGEMENT.APPLICATION_DETAILS.REQUEST_FOR_GENERATING_DOCUMENTS_FAILED',
            translateParams: {
              errorName,
            },
          });
        })
      ),
    { dispatch: false }
  );

  generateChecklist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.generateChecklist),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.generateChecklist(userId, userProcessId).pipe(
          switchMap(() => [
            managementActions.getPdfGenerations({ userProcessId, userId }),
            managementActions.getDocumentsGenerationStatus({ userProcessId, userId }),
            managementActions.getPdfGenerationCurrentProgress({ userProcessId, userId }),
          ]),
          catchError(error => of(managementActions.generateChecklistError({ error })))
        );
      })
    )
  );

  generateChecklistError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(managementActions.generateChecklistError),
        tap(({ error }) => {
          const backendMessage = error?.error?.message;

          if (error?.status === 409 && backendMessage === FULL_PROCESS_GENERATION_IN_PROGRESS) {
            this.notification.showInfo({
              message: 'MANAGEMENT.APPLICATION_DETAILS.CHECKLIST_FULL_PROCESS_IN_PROGRESS',
            });
            return;
          }

          if (error?.status === 400 && backendMessage === NO_CHECKLIST_DOCUMENT_FOR_PROCESS) {
            this.notification.showError({
              message: 'MANAGEMENT.APPLICATION_DETAILS.NO_CHECKLIST_DOCUMENT_FOR_PROCESS',
            });
            return;
          }

          this.notification.showError({
            message: 'MANAGEMENT.APPLICATION_DETAILS.REQUEST_FOR_GENERATING_CHECKLIST_FAILED',
            translateParams: {
              errorName: backendMessage ?? error?.toString(),
            },
          });
        })
      ),
    { dispatch: false }
  );

  getPdfGenerations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getPdfGenerations),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.getPdfGenerations(userId, userProcessId).pipe(
          map(overview => managementActions.getPdfGenerationsSuccess({ userProcessId, overview })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getPdfGenerations.type);
            return of(managementActions.getPdfGenerationsError({ errorMessage }));
          })
        );
      })
    )
  );

  getPdfGenerationCurrentProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getPdfGenerationCurrentProgress),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.getPdfGenerationCurrentProgress(userId, userProcessId).pipe(
          map(progress => managementActions.getPdfGenerationCurrentProgressSuccess({ userProcessId, progress })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getPdfGenerationCurrentProgress.type);
            return of(managementActions.getPdfGenerationCurrentProgressError({ errorMessage }));
          })
        );
      })
    )
  );

  getDocumentsGenerationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getDocumentsGenerationStatus),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.getDocumentsGenerationStatus(userId, userProcessId).pipe(
          map(status => managementActions.getDocumentsGenerationStatusSuccess({ userProcessId, status })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getDocumentsGenerationStatus.type);
            return of(managementActions.getDocumentsGenerationStatusError({ errorMessage }));
          })
        );
      })
    )
  );

  printAllDocumentsOfForeigner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.printAllDocumentsOfForeinger),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.printAllDocumentsOfForeinger(userId, userProcessId).pipe(
          map(file => {
            window.open(window.URL.createObjectURL(file));
            return managementActions.printAllDocumentsOfForeingerSuccess();
          }),
          catchError(() => {
            this.notification.showError({ message: 'COMMON.ERROR.ERROR_DOWNLOADING_FILE' });
            return of(managementActions.printAllDocumentsOfForeingerError());
          })
        );
      })
    )
  );

  printChecklist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.printChecklist),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.printChecklist(userId, userProcessId).pipe(
          map(file => {
            window.open(window.URL.createObjectURL(file));
            return managementActions.printChecklistSuccess();
          }),
          catchError(() => {
            return of(managementActions.printChecklistError());
          })
        );
      })
    )
  );

  areDocsGenerated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.areDocsGenerated),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.areDocsGenerated(userId, userProcessId).pipe(
          map(({ generated }) => {
            return managementActions.areDocsGeneratedSuccess({ generated });
          }),
          catchError(() => {
            return of(
              managementActions.areDocsGeneratedError({ errorMessage: 'ERROR_CHECKING_IF_DOCUMENTS_GENERATED' })
            );
          })
        );
      })
    )
  );

  getMergedFileData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getMergedFileData),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.printAllDocumentsOfForeinger(userId, userProcessId).pipe(
          map(file => {
            return managementActions.getMergedFileDataSuccess({ fileLink: window.URL.createObjectURL(file) });
          }),
          catchError(() => {
            return of(managementActions.getMergedFileDataError({ errorMessage: 'DOWNLOAD_MERGED_FILE_ERROR' }));
          })
        );
      })
    )
  );

  getUserProcessChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getUserProcessChanges),
      switchMap(({ userProcessId, changeTypes, changeAuthorId }) => {
        return this.managementHttp.getUserProcessChanges(userProcessId, changeTypes, changeAuthorId).pipe(
          map(userProcessChanges => {
            return managementActions.getUserProcessChangesSuccess({ userProcessChanges, userProcessId });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getUserProcessChanges.type);
            return of(managementActions.getUserProcessChangesError({ errorMessage, userProcessId }));
          })
        );
      })
    )
  );

  getUserActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getUserActivity),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.managementHttp.getUserActivity(rest).pipe(
          map(userActivity => {
            return managementActions.getUserActivitySuccess({ userActivity });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getUserProcessChanges.type);
            return of(managementActions.getUserActivityError({ errorMessage }));
          })
        );
      })
    )
  );

  setVisitDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.setVisitDate),
      switchMap(({ type, ...opts }) => {
        return this.managementHttp.setVisitDate(opts).pipe(
          map(updatedUserProcess => managementActions.setVisitDateSuccess({ updatedUserProcess })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.setVisitDate.type);
            return of(managementActions.setVisitDateError({ errorMessage }));
          })
        );
      })
    )
  );

  deleteImmigrationOfficeVisitDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.deleteImmigrationOfficeVisitDate),
      switchMap(({ type, ...opts }) => {
        return this.managementHttp.deleteImmigrationOfficeVisitDate(opts).pipe(
          map(updatedUserProcess => managementActions.deleteImmigrationOfficeVisitDateSuccess({ updatedUserProcess })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.deleteImmigrationOfficeVisitDate.type);
            return of(managementActions.deleteImmigrationOfficeVisitDateError({ errorMessage }));
          })
        );
      })
    )
  );

  sendAppointmentReminder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.sendAppointmentReminder),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.sendAppointmentReminder({ userId, userProcessId }).pipe(
          map(({ emailSent }) => managementActions.sendAppointmentReminderSuccess({ emailSent })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.sendAppointmentReminder.type);
            return of(managementActions.sendAppointmentReminderError({ errorMessage }));
          })
        );
      })
    )
  );

  sendImmigrofVisitReminder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.sendImmigrofVisitReminder),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.sendImmigrofVisitReminder({ userId, userProcessId }).pipe(
          map(({ emailSent }) => managementActions.sendImmigrofVisitReminderSuccess({ emailSent })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.sendImmigrofVisitReminder.type);
            return of(managementActions.sendImmigrofVisitReminderError({ errorMessage }));
          })
        );
      })
    )
  );

  confirmVoivoChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.confirmVoivoChange),
      switchMap(({ userId, userProcessId }) => {
        return this.managementHttp.confirmVoivoChange(userId, userProcessId).pipe(
          map(() => managementActions.confirmVoivoChangeSuccess({ userId, userProcessId })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.confirmVoivoChange.type);
            return of(managementActions.confirmVoivoChangeError({ userId, userProcessId, errorMessage }));
          })
        );
      })
    )
  );

  updateDocEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.updateDocEnabled),
      concatMap(({ opts }) => {
        return this.voivodeshipHttp.updateDocumentEnabled(opts).pipe(
          map(updatedDoc => managementActions.updateDocEnabledSuccess({ updatedDoc })),
          catchError(() => {
            this.notification.showError({ message: 'COMMON.ERROR.UNKNOWN-ERROR-UPDATING-ENABLED-DOC' });
            return of(managementActions.updateDocEnabledError({ errorMessage: 'ERROR_UPDATING_VOIVODESHIP' }));
          })
        );
      })
    )
  );

  updateDocEnabledBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.updateDocEnabledBulk),
      concatMap(({ opts }) => {
        return this.voivodeshipHttp.updateDocumentsBulkEnabled(opts).pipe(
          map(updatedDocs => managementActions.updateDocEnabledBulkSuccess({ updatedDocs })),
          catchError(() => {
            this.notification.showError({ message: 'COMMON.ERROR.UNKNOWN-ERROR-UPDATING-ENABLED-DOC' });
            return of(managementActions.updateDocEnabledError({ errorMessage: 'ERROR_UPDATING_VOIVODESHIP' }));
          })
        );
      })
    )
  );

  updateForeignerDocumentQuestionAnswer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.updateForeignerDocumentQuestionAnswer),
      concatMap(({ opts }) => {
        return this.managementHttp.updateForeignerDocumentQuestionAnswer(opts).pipe(
          map(updatedAnswer =>
            managementActions.updateForeignerDocumentQuestionAnswerSuccess({
              updatedAnswer,
              userProcessId: opts.userProcessId,
            })
          ),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.updateForeignerDocumentQuestionAnswer.type);
            this.notification.showError({ message: 'COMMON.ERROR.ERROR_UPDATING_USER_ANSWER' });
            return of(managementActions.updateForeignerDocumentQuestionAnswerError({ errorMessage }));
          })
        );
      })
    )
  );

  getItemsSentForFinalReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getItemsSentForFinalReview),
      switchMap(({ userProcessId }) => {
        return this.managementHttp.getItemsSentForFinalReview({ userProcessId }).pipe(
          map(userAssets => {
            return managementActions.getItemsSentForFinalReviewSuccess({ userAssets });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getItemsSentForFinalReview.type);
            return of(managementActions.getItemsSentForFinalReviewError({ errorMessage }));
          })
        );
      })
    )
  );

  getDecisionDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getDecisionDocuments),
      switchMap(({ userProcessId }) => {
        return this.managementHttp.getDecisionDocuments({ userProcessId }).pipe(
          map(userAssets => {
            return managementActions.getDecisionDocumentsSuccess({ userAssets });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getDecisionDocuments.type);
            return of(managementActions.getDecisionDocumentsError({ errorMessage }));
          })
        );
      })
    )
  );

  uploadDecision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.uploadDecision),
      switchMap(({ opts }) => {
        return this.managementHttp.uploadDecision(opts).pipe(
          map(userProcess => managementActions.uploadDecisionSuccess({ userProcess })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.uploadDecision.type);
            this.notification.showError({ message: 'MANAGEMENT.DECISION_MODAL.SAVING_ERROR' });
            return of(managementActions.uploadDecisionError({ errorMessage }));
          })
        );
      })
    )
  );

  downloadItemSentForFinalReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.downloadItemSentForFinalReview),
      switchMap(({ opts }) => {
        return this.managementHttp.downloadItemSentForFinalReview(opts).pipe(
          map(file => {
            window.open(window.URL.createObjectURL(file));
            return managementActions.downloadItemSentForFinalReviewSuccess();
          }),
          catchError(() => {
            // Show notifciation with error message
            // const errorMessage = parseError(error, userProcessActions.downloadFile.type);
            this.notification.showError({ message: 'COMMON.ERROR.ERROR_DOWNLOADING_FILE' });
            return of(managementActions.downloadItemSentForFinalReviewError());
          })
        );
      })
    )
  );

  updateDocumentsReviewComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.updateDocumentsReviewComment),
      concatMap(({ opts }) => {
        return this.managementHttp.updateDocumentsReviewComment(opts).pipe(
          map(userProcess => managementActions.updateDocumentsReviewCommentSuccess({ userProcess })),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.updateDocumentsReviewComment.type);
            this.notification.showError({ message: 'COMMON.ERROR.ERROR_UPDATING_DOCUMENTS_REVIEW_COMMENT' });
            return of(managementActions.updateDocumentsReviewCommentError({ errorMessage }));
          })
        );
      })
    )
  );

  getProcessWithDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.getProcessWithDocs),
      switchMap(({ processId }) => {
        return this.processHttp.getProcessWithDocs(processId).pipe(
          map(process => {
            return managementActions.getProcessWithDocsSuccess({ process });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.getProcessWithDocs.type);
            return of(managementActions.getProcessWithDocsError({ errorMessage }));
          })
        );
      })
    )
  );

  updateUserProcessServices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.updateUserProcessServices),
      switchMap(({ opts }) => {
        return this.managementHttp.updateIncludedServices(opts).pipe(
          map(updatedUserProcess => {
            return managementActions.updateUserProcessServicesSuccess({ updatedUserProcess });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.updateUserProcessServices.type);
            this.notification.showError({ message: errorMessage });
            return of(managementActions.updateUserProcessServicesError({ errorMessage }));
          })
        );
      })
    )
  );

  setMainStayProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.setMainStayProcess),
      switchMap(({ opts }) => {
        return this.managementHttp.setMainStayProcess(opts).pipe(
          map(updatedUserProcess =>
            managementActions.setMainStayProcessSuccess({
              userId: opts.userId,
              userProcessId: opts.userProcessId,
              isMainStayProcessOfUser: updatedUserProcess.isMainStayProcessOfUser,
            })
          ),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.setMainStayProcess.type);
            this.notification.showError({ message: errorMessage });
            return of(managementActions.setMainStayProcessError({ errorMessage }));
          })
        );
      })
    )
  );

  isAnyProcessOfMineMissingAssistant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.isAnyProcessOfMineMissingAssistant),
      switchMap(() => {
        return this.managementHttp.isAnyProcessOfMineMissingAssistant().pipe(
          map(anyMissing => {
            return managementActions.isAnyProcessOfMineMissingAssistantSuccess({ anyMissing });
          }),
          catchError(error => {
            const errorMessage = parseError(error, managementActions.isAnyProcessOfMineMissingAssistant.type);
            this.notification.showError({ message: errorMessage });
            return of(managementActions.isAnyProcessOfMineMissingAssistantError());
          })
        );
      })
    )
  );

  sendFollowUpEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managementActions.sendFollowUpEmail),
      switchMap(({ usersId, messages }) => {
        const loadingRef: NzMessageRef = this.notification.showLoading({
          message: 'MANAGEMENT.FOLLOW_UP.FOLLOW_UP_SENDING',
          duration: 0,
        });

        return this.managementHttp.sendFollowUpEmail(usersId, messages).pipe(
          delay(2000),
          map(response => {
            this.notification.close(loadingRef.messageId);

            const failedEmails = response.filter(result => !result.success);

            if (failedEmails.length > 0) {
              failedEmails.forEach(failed => {
                this.notification.showError({
                  message: 'COMMON.ERROR.SENDING_MESSAGE',
                  translateParams: { userID: failed.userID },
                });
              });

              const errorMessage = failedEmails.map(failed => ({
                userID: failed.userID,
                error: failed.error || 'Unknown error',
              }));

              return managementActions.sendFollowUpEmailError({ errorMessage });
            } else {
              this.notification.showSuccess({ message: 'MANAGEMENT.FOLLOW_UP.FOLLOW_UP_SUCCESS' });
              return managementActions.sendFollowUpEmailSuccess();
            }
          }),
          catchError(error => {
            this.notification.close(loadingRef.messageId);
            this.notification.showError({ message: 'COMMON.ERROR.SENDING_MESSAGE' });

            return of(managementActions.sendFollowUpEmailError({ errorMessage: error }));
          })
        );
      })
    )
  );
}
